// coded by Narain on (11/09/2024)

import { styled, keyframes } from '@mui/system';
import { Box, TextField, Button, Paper, Avatar, Typography } from '@mui/material';

// Animations
const typingAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% { opacity: 0; }
  40%, 60% { opacity: 1; }
`;

const slideInFromBelow = keyframes`
  0% { opacity: 0; transform: translateY(50%); }
  100% { opacity: 1; transform: translateY(0); }
`;

export const ChatContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

export const MessageList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(1),
  width: '100%',
  maxHeight: 'calc(100vh - 200px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    maxHeight: 'calc(100vh - 200px)',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
    maxHeight: 'calc(100vh - 250px)',
  },
}));

// Messages
export const MessageBubble = styled(Box)(({ theme, sender }) => ({
  display: 'flex',
  alignItems: 'center',
  maxWidth: '60%',
  padding: theme.spacing(1.5),
  borderRadius: '18px',
  marginBottom: theme.spacing(2),
  backgroundColor: sender === 'user' ? theme.palette.primary.main : theme.palette.grey[200],
  color: sender === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
  alignSelf: sender === 'user' ? 'flex-end' : 'flex-start',
  textAlign: sender === 'user' ? 'left' : 'inherit',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  animation: `${slideInFromBelow} 0.5s ease`,
  [theme.breakpoints.down('sm')]: {
    maxWidth: '80%',
    padding: theme.spacing(1),
  },
}));

export const AvatarWrapper = styled(Avatar)(({ theme, sender }) => ({
  marginRight: sender === 'bot' ? theme.spacing(2) : 0,
  marginLeft: sender === 'user' ? theme.spacing(2) : 0,
  backgroundColor: sender === 'user' ? theme.palette.primary.main : theme.palette.common.white,
  [theme.breakpoints.down('sm')]: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
}));

export const StyledImage = styled('img')({
  height: '25px',
  width: '25px',
  objectFit: 'contain',
});

// Input and Send Button
export const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '95%',
  position: 'absolute',
  bottom: '5%',
  borderRadius: '20px',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: '7%',
    maxWidth: '95%',
    padding: theme.spacing(1),
  },
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    bottom: '7%',
    maxWidth: '95%',
    padding: theme.spacing(1),
  },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  borderRadius: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '20px',
    paddingRight: '48px',
    [theme.breakpoints.down('sm')]: {
      paddingRight: '40px',
    },
  },
}));

export const SendButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  right: '2%',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '40px',
  height: '40px',
  backgroundColor: '#FF8C00',
  color: theme.palette.common.white,
  borderRadius: '50%',
  minWidth: 'auto',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  border: 'none',
  padding: 0,
  [theme.breakpoints.down('sm')]: {
    width: '35px',
    height: '35px',
    right: '5%',
  },
}));

export const SendButtonImage = styled('img')({
  width: '50%',
  height: '50%',
  objectFit: 'contain',
});

// Loader
export const DottedLoader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  '& span': {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    margin: '0 2px',
    borderRadius: '50%',
    backgroundColor: theme.palette.text.secondary,
    animation: `${typingAnimation} 1.5s infinite`,
  },
  '& span:nth-of-type(2)': {
    animationDelay: '0.2s',
  },
  '& span:nth-of-type(3)': {
    animationDelay: '0.4s',
  },
}));

export const FooterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'fixed',
  bottom: '1%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: '1%',
    maxWidth: '95%',
    padding: theme.spacing(1),
  },
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    bottom: '1%',
    maxWidth: '95%',
    padding: theme.spacing(1),
  },
}));

export const FooterText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: 'fixed',
  bottom: '1%',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: '1%',
    maxWidth: '95%',
    padding: theme.spacing(1),
  },
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    bottom: '1%',
    maxWidth: '95%',
    padding: theme.spacing(1),
  },
}));
